import {AddClientContactDto, ClientContactDto, UpdateClientContactDto} from '../dtos/client-contact.dto';
import {streetCarriesCity} from '../../../shared/misc/location-helpers';

/*
 * Additional Client Info as Android keeps it (clients/ContactEntries.kt): four
 * flat lists (names, phones, addresses, emails) of single values, each with a
 * title saying who or what it is for ("Wife's cell", "Office"). Storage is
 * `client_contact`, one entry per row since V35; a row that still holds
 * several fields is read as one entry per field, and those entries write back
 * into that same row. Change this file and ContactEntries.kt together.
 */

export type ContactKind = 'name' | 'phone' | 'address' | 'email';

/** The section's order, and the Add box's. */
export const CONTACT_KINDS: ContactKind[] = ['name', 'phone', 'address', 'email'];

export const KIND_LABEL: Record<ContactKind, string> = {name: 'Name', phone: 'Phone', address: 'Address', email: 'Email'};

export interface ContactEntry {
  /** `<rowId>-<kind>` for a saved entry, `new-<n>` for one being added, `main-<kind>` for the client's own field. */
  key: string;
  /** The `client_contact` row it lives in; null until Save creates it, and for the client's own field. */
  rowId: number | null;
  kind: ContactKind;
  /** The name, the phone, the email, or the street. */
  value: string;
  city: string;
  state: string;
  postalCode: string;
  title: string;
}

/** The client's own fields, as the card edits them. */
export interface ClientFields {
  name: string;
  phoneNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  lat: number;
  lng: number;
}

const blank = (value?: string | null) => !value || !value.trim();

/** What the entry shows as its label: the title, or the field type without one. */
export const displayTitle = (entry: ContactEntry) => entry.title.trim() || KIND_LABEL[entry.kind];

const sameEntry = (a: ContactEntry | undefined, b: ContactEntry) =>
  !!a && a.key === b.key && a.rowId === b.rowId && a.kind === b.kind && a.value === b.value
  && a.city === b.city && a.state === b.state && a.postalCode === b.postalCode && a.title === b.title;

/** The entries the saved rows hold, in the section's order. */
export const entriesFrom = (rows: ClientContactDto[]): ContactEntry[] => {
  const entries = rows.flatMap(row => {
    const entry = (kind: ContactKind, value?: string): ContactEntry => ({
      key: `${row.id}-${kind}`, rowId: row.id, kind, value: value ?? '', city: '', state: '', postalCode: '', title: row.title ?? '',
    });
    const out: ContactEntry[] = [];
    if (!blank(row.name)) out.push(entry('name', row.name));
    if (!blank(row.phoneNumber)) out.push(entry('phone', row.phoneNumber));
    if ([row.address, row.city, row.state, row.postalCode].some(part => !blank(part))) {
      out.push({...entry('address', row.address), city: row.city ?? '', state: row.state ?? '', postalCode: row.postalCode ?? ''});
    }
    if (!blank(row.email)) out.push(entry('email', row.email));
    return out;
  });
  return inSectionOrder(entries);
};

export const inSectionOrder = (entries: ContactEntry[]) =>
  CONTACT_KINDS.flatMap(kind => entries.filter(entry => entry.kind === kind));

// ---- Phones -----------------------------------------------------------------------

/** Digits behind a phone field: a leading 1 on 11 or more is a country code, and ten are kept. */
export const phoneDigits = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  return (digits.length >= 11 && digits.startsWith('1') ? digits.slice(1) : digits).slice(0, 10);
};

/** "(208) 880-1195", filling in as it is typed (Android's formatPhoneNumber). */
export const formatPhoneNumber = (value: string): string => {
  const digits = phoneDigits(value);
  if (!digits) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Title Case for names, streets and cities as they are typed. */
export const capitalizeWords = (value: string) =>
  value.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

// ---- Addresses ----------------------------------------------------------------------

/** Trimmed, with the geocoder's "UNKNOWN" placeholder read as blank. */
const addressPart = (value?: string | null) => {
  const trimmed = value?.trim() ?? '';
  return trimmed.toUpperCase() === 'UNKNOWN' ? '' : trimmed;
};

/** The street, and "City, ST 83704" unless the street already carries the city (SQ-502). */
export const addressParts = (entry: Pick<ContactEntry, 'value' | 'city' | 'state' | 'postalCode'>): {street: string; rest: string} => {
  const street = addressPart(entry.value);
  if (streetCarriesCity(street, addressPart(entry.city))) return {street, rest: ''};
  const cityState = [addressPart(entry.city), addressPart(entry.state)].filter(Boolean).join(', ');
  return {street, rest: [cityState, addressPart(entry.postalCode).slice(0, 5)].filter(Boolean).join(' ')};
};

/** The address on one line: what is geocoded, and what the map opens. */
export const addressLine = (entry: Pick<ContactEntry, 'value' | 'city' | 'state' | 'postalCode'>): string => {
  const {street, rest} = addressParts(entry);
  return [street, rest].filter(Boolean).join(', ');
};

/** A suggestion picked on an address field, split into its parts. */
export interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  lat?: number;
  lng?: number;
}

/** A picked suggestion fills the street, and city, state and zip when it carries them; a blank part leaves what was typed. */
export const withParsedAddress = (entry: ContactEntry, parsed: ParsedAddress): ContactEntry => ({
  ...entry,
  value: parsed.street.trim() || entry.value,
  city: parsed.city.trim() || entry.city,
  state: parsed.state.trim() || entry.state,
  postalCode: parsed.postalCode.trim() || entry.postalCode,
});

// ---- Save ---------------------------------------------------------------------------------

export interface ContactCommit {
  creates: AddClientContactDto[];
  updates: {id: number; request: UpdateClientContactDto}[];
  deletes: number[];
}

/** The on-save cleanup: trimmed throughout, the phone masked, the email lowercased. */
const normalized = (entry: ContactEntry): ContactEntry => ({
  ...entry,
  value: entry.kind === 'phone' ? formatPhoneNumber(entry.value)
    : entry.kind === 'email' ? entry.value.trim().toLowerCase()
      : entry.value.trim(),
  city: entry.city.trim(),
  state: entry.state.trim().toUpperCase(),
  postalCode: entry.postalCode.trim(),
  title: entry.title.trim(),
});

/**
 * The create / update / delete calls that bring `rows` in line with `entries`.
 * An entry left empty is dropped (clearing a field and saving is how an entry
 * is deleted), but only one the person emptied: an entry that still matches
 * its row is left alone (a web-saved address with a city and no street stays).
 */
export const planContactCommit = (rows: ClientContactDto[], entries: ContactEntry[]): ContactCommit => {
  const creates = entries
    .filter(entry => entry.rowId === null && !blank(entry.value))
    .map((entry): AddClientContactDto => {
      const v = normalized(entry);
      const is = (kind: ContactKind) => entry.kind === kind;
      return {
        name: is('name') ? v.value : undefined,
        phoneNumber: is('phone') ? v.value : undefined,
        email: is('email') ? v.value : undefined,
        address: is('address') ? v.value : undefined,
        city: is('address') ? v.city : undefined,
        state: is('address') ? v.state : undefined,
        postalCode: is('address') ? v.postalCode : undefined,
        title: v.title || undefined,
      };
    });

  const updates: ContactCommit['updates'] = [];
  const deletes: number[] = [];
  for (const row of rows) {
    const rowEntries = entries.filter(entry => entry.rowId === row.id);
    if (rowEntries.length === 0) continue;
    const original = entriesFrom([row]);
    const untouched = (entry: ContactEntry) => sameEntry(original.find(o => o.kind === entry.kind), entry);
    // What stays in the row: entries still filled, and ones never touched.
    const kept = rowEntries.filter(entry => !blank(entry.value) || untouched(entry));
    if (kept.length === 0) {
      deletes.push(row.id);
      continue;
    }
    // Untouched entries go back exactly as stored; only edits are cleaned up.
    const field = (kind: ContactKind) => {
      const entry = kept.find(e => e.kind === kind);
      return entry && (untouched(entry) ? entry : normalized(entry));
    };
    const address = field('address');
    const request: Required<UpdateClientContactDto> = {
      name: field('name')?.value ?? '',
      phoneNumber: field('phone')?.value ?? '',
      email: field('email')?.value ?? '',
      address: address?.value ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      postalCode: address?.postalCode ?? '',
      // A row's entries share its title.
      title: kept[0].title.trim(),
    };
    const current: Required<UpdateClientContactDto> = {
      name: row.name ?? '', phoneNumber: row.phoneNumber ?? '', email: row.email ?? '', address: row.address ?? '',
      city: row.city ?? '', state: row.state ?? '', postalCode: row.postalCode ?? '', title: row.title ?? '',
    };
    if ((Object.keys(request) as (keyof UpdateClientContactDto)[]).some(key => request[key] !== current[key])) {
      updates.push({id: row.id, request});
    }
  }
  return {creates, updates, deletes};
};

// ---- The create question: slots (SQ-426) ----------------------------------------------------
//
// Each list's candidates are the client's own field (the "main" slot, stored
// on the client) plus its saved entries. Creating an appointment or an
// estimate for a client with more than one candidate somewhere asks which the
// record is for; the profile itself always shows the client's own.

export const mainKey = (kind: ContactKind) => `main-${kind}`;

export const isMainKey = (key: string) => key.startsWith('main-');

/** The client's own field for `kind`, as an entry (no row, no title). */
export const mainEntry = (client: ClientFields, kind: ContactKind): ContactEntry => {
  const base: ContactEntry = {key: mainKey(kind), rowId: null, kind, value: '', city: '', state: '', postalCode: '', title: ''};
  switch (kind) {
    case 'name': return {...base, value: client.name};
    case 'phone': return {...base, value: client.phoneNumber};
    case 'email': return {...base, value: client.email};
    case 'address': return {...base, value: client.address, city: client.city, state: client.state, postalCode: client.postalCode};
  }
};

export type Picks = Partial<Record<ContactKind, string>>;

/** What a create question is being asked about, and what has been ticked so far. */
export interface CreateQuestion {
  client: ClientFields;
  entries: ContactEntry[];
  /** Null: nothing is being asked. */
  prompt: RecordKind | null;
  picks: Picks;
}

export type RecordKind = 'appointment' | 'estimate';

/** Everything that can be picked for `kind`: the client's own field when filled, then the saved entries. */
export const slots = (q: Pick<CreateQuestion, 'client' | 'entries'>, kind: ContactKind): ContactEntry[] => {
  const main = mainEntry(q.client, kind);
  const mainFilled = !blank(main.value) || !!addressLine(main);
  return [
    ...(mainFilled ? [main] : []),
    ...q.entries.filter(entry => entry.kind === kind && entry.rowId !== null && !blank(entry.value)),
  ];
};

/** Whether creating a record has anything to ask: some list offers a real choice. */
export const hasChoices = (q: Pick<CreateQuestion, 'client' | 'entries'>) => CONTACT_KINDS.some(kind => slots(q, kind).length > 1);

/** Whether the question shows checkboxes on `kind`: only a list with more than one candidate. */
export const isPickable = (q: CreateQuestion, kind: ContactKind) => q.prompt !== null && slots(q, kind).length > 1;

/**
 * Whether `entry` is ticked. Addresses start unticked; a name, phone or email
 * starts on the client's own. A tick stays where it is put.
 */
export const isPicked = (q: CreateQuestion, entry: ContactEntry) =>
  (q.picks[entry.kind] ?? (entry.kind === 'address' ? undefined : mainKey(entry.kind))) === entry.key;

/** The entry the record about to be created is for: the tick, else the client's own. */
export const pickedEntry = (q: CreateQuestion, kind: ContactKind): ContactEntry => {
  const key = q.picks[kind];
  return (key && slots(q, kind).find(entry => entry.key === key)) || mainEntry(q.client, kind);
};

/** The question can go ahead: an address is chosen, or there is only one. */
export const promptReady = (q: CreateQuestion) => slots(q, 'address').length <= 1 || q.picks.address !== undefined;

/** The note above the buttons, from what this client actually has a choice about; null once an address is picked. */
export const promptNote = (q: CreateQuestion): string | null => {
  if (!q.prompt) return null;
  const manyAddresses = slots(q, 'address').length > 1;
  if (manyAddresses && q.picks.address !== undefined) return null;
  const others = (['name', 'phone', 'email'] as ContactKind[])
    .filter(kind => slots(q, kind).length > 1)
    .map(kind => KIND_LABEL[kind].toLowerCase());
  const listed = others.length <= 1 ? others.join('') : `${others.slice(0, -1).join(', ')} or ${others[others.length - 1]}`;
  if (manyAddresses && others.length === 0) return `Select the address for this ${q.prompt}.`;
  if (manyAddresses) return `Select the address for this ${q.prompt}. The preselected ${listed} can be changed too.`;
  return `The preselected ${listed} can be changed for this ${q.prompt} if you want.`;
};
